import { Configuration as CatalogueConfiguration } from '@teamjourney/catalogue-client-typescript';

/**
 * Configuration factories for each service type.
 * Maps service types to their specific configuration creators.
 *
 * @example To add a new API configuration:
 * const configFactories: ConfigFactory = {
 *   payments: (config) => new PaymentsConfiguration(config),
 *   users: (config) => new UsersConfiguration({
 *     token: config.accessToken,
 *     apiUrl: config.basePath
 *   })
 * };
 */
type ServiceTypes = 'catalogue';

type ConfigFactory = {
  [K in ServiceTypes]: (config: { accessToken?: string; basePath: string }) => unknown;
};
export const configFactories: ConfigFactory = {
  catalogue: (config) =>
    new CatalogueConfiguration({
      accessToken: config.accessToken,
      basePath: config.basePath,
    }),
};

/**
 * Creates an API client instance with the specified configuration.
 * @template T - The type of API client to create
 * @param ApiClass - The API client class constructor (this type comes from the sdk)
 * @param serviceType - The type of service configuration to use (e.g., 'payments')
 * @param config - Configuration options
 * @returns Configured API client instance
 */
export const createApiClient = <T>(
  ApiClientClass: new (config: any) => T,
  serviceType: ServiceTypes,
  config: {
    accessToken?: string;
    basePath: string;
  }
) => {
  const configFactory = configFactories[serviceType];
  return new ApiClientClass(configFactory(config));
};

/**
 * To add support for a new API:
 * 1. Add the service type to ServiceTypes
 * 2. Add new configuration to configFactories
 * 3. Use client in application code
 * 4. See merchantSettingsClient.api.ts as an example
 * */
